import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const FEATURED = movies.filter((m) => m.heroes && m.heroes.length > 0).slice(0, 5);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<number | null>(null);
  const touchStartX = useRef(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % FEATURED.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + FEATURED.length) % FEATURED.length), []);

  useEffect(() => {
    timerRef.current = window.setInterval(next, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [next]);

  const restart = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(next, 6000);
  };

  const handleNext = () => {
    next();
    restart();
  };
  const handlePrev = () => {
    prev();
    restart();
  };

  const current = FEATURED[index];

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden md:h-[560px]"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        const diff = e.changedTouches[0].clientX - touchStartX.current;
        if (diff < -50) handleNext();
        if (diff > 50) handlePrev();
      }}
    >
      {FEATURED.map((m, i) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={m.heroes[0]}
            alt={`${m.title}剧情背景图`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-6xl flex-col justify-center px-4 py-10 md:px-6">
        <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50">
          <i className="ri-fire-fill"></i>
          焦点推荐
        </span>
        <h1 className="max-w-2xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
          {current.title}
        </h1>
        <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600">
          <span>{current.category}</span>
          <span className="text-foreground-400">·</span>
          <span>{current.year}</span>
          <span className="text-foreground-400">·</span>
          <span>{current.region}</span>
          <span className="text-foreground-400">·</span>
          <span>{current.duration}</span>
          <span className="text-foreground-400">·</span>
          <span className="text-accent-500">
            <i className="ri-star-fill mr-1"></i>
            {current.rating.toFixed(1)}
          </span>
        </p>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
          {current.synopsis}
        </p>
        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
          <Link
            to={`/detail/${current.id}`}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
          >
            <i className="ri-play-circle-line text-lg"></i>
            查看剧情详情
          </Link>
          <Link
            to="/category"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100/70 px-6 py-3 text-sm font-semibold text-foreground-900 transition-colors hover:border-primary-500"
          >
            浏览全部作品
          </Link>
        </div>

        <div className="mt-8 flex items-center gap-2">
          {FEATURED.map((m, i) => (
            <button
              key={m.id}
              type="button"
              aria-label={`切换到${m.title}`}
              onClick={() => {
                setIndex(i);
                restart();
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-8 bg-accent-500' : 'w-4 bg-background-300 hover:bg-background-400'
              }`}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={handlePrev}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-300 bg-background-100/80 text-foreground-950 backdrop-blur transition-colors hover:bg-background-200 md:flex md:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={handleNext}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-300 bg-background-100/80 text-foreground-950 backdrop-blur transition-colors hover:bg-background-200 md:flex md:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>
    </section>
  );
}